import { useEffect, useState, useCallback, useRef } from 'react';
import { useAppDispatch } from '../store';
import { logoutAsync, setPendingLogoutRedirect } from '../store/authSlice';
import StorageService from '../services/storage';
import { SessionSettings } from '../types/auth';

const TICK_INTERVAL_MS = 1000; // 1-second tick for responsive countdowns

/**
 * Which timeout is currently driving the warning / logout sequence.
 * 'none' means no warning is visible.
 */
type WarningSource = 'none' | 'absolute' | 'idle';

interface SessionTimeoutState {
  /** True when a countdown warning should be displayed. */
  isWarningVisible: boolean;
  /** Seconds remaining in the active countdown (0 when hidden). */
  timeRemaining: number;
  /** Which feature triggered the current warning. */
  warningSource: WarningSource;
  /** Dismiss the idle warning and reset the idle timer (no-op for absolute). */
  extendSession: () => void;
  /** Force immediate logout. */
  forceLogout: () => void;
}

// ---------------------------------------------------------------------------
// Helper: read session settings safely
// ---------------------------------------------------------------------------
function getSettings(): SessionSettings {
  const stored = StorageService.getSessionSettings();
  return {
    // Default to DISABLED until the backend confirms the hospital's policy.
    // This prevents surprise auto-logouts when localStorage has no settings
    // (e.g. first load, incognito, or cleared storage).
    max_login_duration_enabled: stored?.max_login_duration_enabled ?? false,
    max_login_duration_minutes: stored?.max_login_duration_minutes ?? 480,
    max_login_warning_minutes: stored?.max_login_warning_minutes ?? 5,
    idle_timeout_enabled: stored?.idle_timeout_enabled ?? false,
    idle_timeout_minutes: stored?.idle_timeout_minutes ?? 20,
    idle_warning_minutes: stored?.idle_warning_minutes ?? 3,
    session_check_interval_minutes: stored?.session_check_interval_minutes ?? 5,
  };
}

// ---------------------------------------------------------------------------
// Hook
// ---------------------------------------------------------------------------
export const useSessionTimeout = (): SessionTimeoutState => {
  const dispatch = useAppDispatch();

  // --- React state --------------------------------------------------------
  const [isWarningVisible, setIsWarningVisible] = useState(false);
  const [timeRemaining, setTimeRemaining] = useState(0);
  const [warningSource, setWarningSource] = useState<WarningSource>('none');

  // --- Refs (mutable across renders without re-render) --------------------
  // Absolute deadline: loginTime + max_login_duration.  Never resets.
  const absoluteDeadlineRef = useRef<number | null>(null);
  // Idle deadline: lastActivity + idle_timeout.  Resets on activity.
  const idleDeadlineRef = useRef<number | null>(null);
  // Last activity timestamp for the idle tracker.
  const lastActivityRef = useRef<number>(Date.now());
  // Track warning source in a ref so the tick callback always sees the latest value.
  const warningSourceRef = useRef<WarningSource>('none');
  // When we last synced idle deadline from storage (so API/validation activity extends idle)
  const lastIdleSyncFromStorageRef = useRef<number>(0);
  const IDLE_SYNC_INTERVAL_MS = 60 * 1000;
  // Mobile browsers can background tabs for long periods. If the idle deadline
  // passes while the tab is hidden, defer the forced logout until the user
  // returns so they can see the warning and explicitly continue or logout.
  const isDocumentHiddenRef = useRef<boolean>(document.visibilityState !== 'visible');
  const pendingHiddenIdleWarningRef = useRef<boolean>(false);
  const pendingHiddenAbsoluteLogoutRef = useRef<boolean>(false);

  // Keep the ref and state in sync.
  const setSource = useCallback((src: WarningSource) => {
    warningSourceRef.current = src;
    setWarningSource(src);
  }, []);

  // -----------------------------------------------------------------------
  // Force logout
  // -----------------------------------------------------------------------
  const forceLogout = useCallback(() => {
    const settings = getSettings();
    const storedUser = StorageService.getUser();
    console.warn(
      `[LOGOUT-DEBUG] useSessionTimeout.forceLogout | reason=forceLogout_called | ` +
      `user=${storedUser?.username ?? 'null'} | userId=${storedUser?.id ?? 'null'} | ` +
      `wasAuthenticated=true | ` +
      `max_login_duration_enabled=${settings.max_login_duration_enabled} | ` +
      `max_login_duration_minutes=${settings.max_login_duration_minutes} | ` +
      `idle_timeout_enabled=${settings.idle_timeout_enabled} | ` +
      `idle_timeout_minutes=${settings.idle_timeout_minutes} | ` +
      `warningSource=${warningSourceRef.current} | ` +
      `timestamp=${new Date().toISOString()}`
    );
    console.trace('[LOGOUT-DEBUG] useSessionTimeout.forceLogout stack trace');

    // Say WHY on the login page. A timed-out session used to dispatch the
    // logout and nothing else: the user landed on the login screen with no
    // explanation and a console full of 401s, which reads as a broken app
    // rather than an expired session. `setPendingLogoutRedirect` is the same
    // channel authInterceptor already uses — App.tsx navigates and appends
    // `?error=`, and SessionEndedNotice turns it into a sentence.
    //
    // The message is a stable token, not prose, because the notice matches on
    // it. Which timeout fired matters to the user: an 8-hour cap is not
    // something they could have prevented by staying active.
    dispatch(
      setPendingLogoutRedirect({
        path: '/onecare/login',
        error:
          warningSourceRef.current === 'idle'
            ? 'session_ended_idle'
            : 'session_ended_max_duration',
      })
    );
    dispatch(logoutAsync());
    setIsWarningVisible(false);
    setTimeRemaining(0);
    setSource('none');
  }, [dispatch, setSource]);

  // -----------------------------------------------------------------------
  // Extend session (dismiss idle warning + reset idle timer)
  // -----------------------------------------------------------------------
  const extendSession = useCallback(() => {
    const now = Date.now();
    const settings = getSettings();

    // Update lastActivity in storage
    const metadata = StorageService.getSessionMetadata();
    StorageService.setSessionMetadata({
      loginTime: metadata?.loginTime ?? now,
      lastActivity: now,
      userAgent: metadata?.userAgent ?? navigator.userAgent,
    });

    lastActivityRef.current = now;

    if (settings.idle_timeout_enabled) {
      idleDeadlineRef.current = now + settings.idle_timeout_minutes * 60 * 1000;
    }

    // Only dismiss the warning if it was triggered by idle timeout.
    // An absolute-duration warning cannot be dismissed by activity.
    if (warningSourceRef.current === 'idle') {
      setIsWarningVisible(false);
      setTimeRemaining(0);
      setSource('none');
    }
  }, [setSource]);

  // -----------------------------------------------------------------------
  // Initialise deadlines on mount
  // -----------------------------------------------------------------------
  useEffect(() => {
    const settings = getSettings();
    const metadata = StorageService.getSessionMetadata();
    const now = Date.now();

    const loginTime = metadata?.loginTime ?? now;
    const lastActivity = metadata?.lastActivity ?? now;
    lastActivityRef.current = lastActivity;

    // If no metadata existed yet, persist it.
    if (!metadata) {
      StorageService.setSessionMetadata({
        loginTime: now,
        lastActivity: now,
        userAgent: navigator.userAgent,
      });
    }

    // Absolute deadline
    if (settings.max_login_duration_enabled) {
      absoluteDeadlineRef.current = loginTime + settings.max_login_duration_minutes * 60 * 1000;
    } else {
      absoluteDeadlineRef.current = null;
    }

    // Idle deadline
    if (settings.idle_timeout_enabled) {
      idleDeadlineRef.current = lastActivity + settings.idle_timeout_minutes * 60 * 1000;
    } else {
      idleDeadlineRef.current = null;
    }

    if (process.env.REACT_APP_ENABLE_DEBUG_LOGS === 'true') {
      const idleMin = settings.idle_timeout_minutes;
      const maxMin = settings.max_login_duration_minutes;
      const idleDeadline = idleDeadlineRef.current;
      const absDeadline = absoluteDeadlineRef.current;
      const idleStr = idleDeadline != null
        ? `idle deadline in ${Math.round((idleDeadline - now) / 60000)} min`
        : 'disabled';
      const absStr = absDeadline != null
        ? `absolute deadline in ${Math.round((absDeadline - now) / 60000)} min`
        : 'disabled';
      console.log(
        `[SESSION] useSessionTimeout init: idle_timeout_minutes=${idleMin} max_login_duration_minutes=${maxMin} idle_timeout_enabled=${settings.idle_timeout_enabled} max_login_duration_enabled=${settings.max_login_duration_enabled} -> ${idleStr}, ${absStr}`
      );
    }
  }, []); // runs once on mount

  // -----------------------------------------------------------------------
  // Main tick (every second)
  // -----------------------------------------------------------------------
  useEffect(() => {
    const tick = () => {
      const now = Date.now();
      const settings = getSettings();

      // --- Sync idle deadline from storage periodically (API/validation updates lastActivity) ---
      if (settings.idle_timeout_enabled && now - lastIdleSyncFromStorageRef.current >= IDLE_SYNC_INTERVAL_MS) {
        lastIdleSyncFromStorageRef.current = now;
        const metadata = StorageService.getSessionMetadata();
        if (metadata?.lastActivity) {
          const storedLastActivity = metadata.lastActivity;
          lastActivityRef.current = storedLastActivity;
          idleDeadlineRef.current = storedLastActivity + settings.idle_timeout_minutes * 60 * 1000;
        }
      }

      // --- Absolute tracker ---
      let absRemaining = Infinity;
      let absWarningThreshold = 0;
      if (settings.max_login_duration_enabled && absoluteDeadlineRef.current !== null) {
        absRemaining = absoluteDeadlineRef.current - now;
        absWarningThreshold = settings.max_login_warning_minutes * 60 * 1000;
      }

      // --- Idle tracker ---
      let idleRemaining = Infinity;
      let idleWarningThreshold = 0;
      if (settings.idle_timeout_enabled && idleDeadlineRef.current !== null) {
        idleRemaining = idleDeadlineRef.current - now;
        idleWarningThreshold = settings.idle_warning_minutes * 60 * 1000;
      }

      // --- Determine which fires first ---
      const minRemaining = Math.min(absRemaining, idleRemaining);

      const absoluteExpired =
        settings.max_login_duration_enabled && absRemaining <= 0;
      const idleExpired =
        settings.idle_timeout_enabled && idleRemaining <= 0;

      // Never fire an unattended logout from a hidden tab.
      //
      // Tabs in one browser SHARE a cookie jar, so a background tab logging
      // itself out ends the session the user is actively working in another
      // tab — which is the 2026-08-16 incident, and is not fixed by scoping
      // the server-side revoke (that protects other devices, not other tabs).
      // Nobody is watching a hidden tab's countdown either, so the logout
      // arrives with no warning and no explanation.
      //
      // Deferring extends nothing: every tab computes the same deadlines from
      // the same login time, so whichever tab IS visible enforces the cap at
      // the same moment. Only when every tab is hidden does enforcement wait,
      // and then it fires the instant the user comes back — the trade-off
      // already accepted for the idle timer.
      if (isDocumentHiddenRef.current) {
        if (absoluteExpired) {
          // The absolute cap cannot be extended (extendSession is a no-op for
          // it), so there is no warning worth showing on resume — offering a
          // countdown the user cannot act on would only be theatre. Log out on
          // return, where the reason is actually seen.
          pendingHiddenAbsoluteLogoutRef.current = true;
          return;
        }
        if (idleExpired) {
          // Idle CAN be extended, so this converts into a visible warning on
          // resume and the user gets to say "I'm still here".
          pendingHiddenIdleWarningRef.current = true;
          return;
        }
      }

      // Force logout if either timer expired
      if (minRemaining <= 0) {
        const reason =
          absRemaining <= 0 && settings.max_login_duration_enabled
            ? 'absolute_max_duration_expired'
            : 'idle_timeout_expired';
        console.warn(
          `[LOGOUT-DEBUG] useSessionTimeout.tick | reason=${reason} | ` +
          `absRemaining=${Math.round(absRemaining / 1000)}s | ` +
          `idleRemaining=${Math.round(idleRemaining / 1000)}s | ` +
          `idle_timeout_enabled=${settings.idle_timeout_enabled} | ` +
          `idle_timeout_minutes=${settings.idle_timeout_minutes} | ` +
          `max_login_duration_enabled=${settings.max_login_duration_enabled} | ` +
          `max_login_duration_minutes=${settings.max_login_duration_minutes} | ` +
          `timestamp=${new Date().toISOString()}`
        );
        forceLogout();
        return;
      }

      // Determine warning state
      const absInWarning = absRemaining <= absWarningThreshold && absRemaining > 0;
      const idleInWarning = idleRemaining <= idleWarningThreshold && idleRemaining > 0;

      if (absInWarning || idleInWarning) {
        // Pick whichever is closer to zero as the active warning
        let activeSource: WarningSource;
        let activeRemaining: number;
        if (absInWarning && idleInWarning) {
          activeSource = absRemaining <= idleRemaining ? 'absolute' : 'idle';
          activeRemaining = Math.min(absRemaining, idleRemaining);
        } else if (absInWarning) {
          activeSource = 'absolute';
          activeRemaining = absRemaining;
        } else {
          activeSource = 'idle';
          activeRemaining = idleRemaining;
        }
        setIsWarningVisible(true);
        setTimeRemaining(Math.ceil(activeRemaining / 1000));
        setSource(activeSource);
      } else {
        // No warning needed
        if (warningSourceRef.current !== 'none') {
          setIsWarningVisible(false);
          setTimeRemaining(0);
          setSource('none');
        }
      }
    };

    const interval = setInterval(tick, TICK_INTERVAL_MS);
    tick(); // run immediately
    return () => clearInterval(interval);
  }, [forceLogout, setSource, IDLE_SYNC_INTERVAL_MS]);

  // -----------------------------------------------------------------------
  // Visibility changes (mobile/background tab handling)
  // -----------------------------------------------------------------------
  useEffect(() => {
    const handleVisibilityChange = () => {
      const isHidden = document.visibilityState !== 'visible';
      isDocumentHiddenRef.current = isHidden;

      if (isHidden) {
        return;
      }

      // The hard cap expired while this tab was in the background. Now that
      // the user is looking at it, end the session — they will land on the
      // login page with the reason, rather than having been signed out at some
      // unknown point behind their back.
      if (pendingHiddenAbsoluteLogoutRef.current) {
        pendingHiddenAbsoluteLogoutRef.current = false;
        pendingHiddenIdleWarningRef.current = false;
        setSource('absolute');
        forceLogout();
        return;
      }

      if (!pendingHiddenIdleWarningRef.current) {
        return;
      }

      const settings = getSettings();
      if (!settings.idle_timeout_enabled) {
        pendingHiddenIdleWarningRef.current = false;
        return;
      }

      const warningMs = Math.max(1, settings.idle_warning_minutes) * 60 * 1000;
      idleDeadlineRef.current = Date.now() + warningMs;
      pendingHiddenIdleWarningRef.current = false;
      setIsWarningVisible(true);
      setTimeRemaining(Math.ceil(warningMs / 1000));
      setSource('idle');

      if (process.env.REACT_APP_ENABLE_DEBUG_LOGS === 'true') {
        console.log(
          `[SESSION] Resumed from hidden tab after idle expiry; showing ${settings.idle_warning_minutes}-minute warning instead of immediate logout`
        );
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [setSource, forceLogout]);

  // -----------------------------------------------------------------------
  // Listen for user activity (resets idle deadline only)
  // -----------------------------------------------------------------------
  useEffect(() => {
    const ACTIVITY_EVENTS = ['click', 'keypress', 'keydown', 'scroll', 'mousemove', 'touchstart'];
    // Throttle: only process activity once per second to avoid excessive work.
    let lastProcessed = 0;

    const handleActivity = () => {
      const now = Date.now();
      if (now - lastProcessed < 1000) return;
      lastProcessed = now;

      const settings = getSettings();
      if (!settings.idle_timeout_enabled) return;

      lastActivityRef.current = now;
      idleDeadlineRef.current = now + settings.idle_timeout_minutes * 60 * 1000;

      // Update storage last activity (throttled further: once per 15 s)
      const metadata = StorageService.getSessionMetadata();
      if (!metadata || now - metadata.lastActivity > 15_000) {
        StorageService.setSessionMetadata({
          loginTime: metadata?.loginTime ?? now,
          lastActivity: now,
          userAgent: metadata?.userAgent ?? navigator.userAgent,
        });
      }

      // If we are currently showing an idle warning, dismiss it.
      if (warningSourceRef.current === 'idle') {
        setIsWarningVisible(false);
        setTimeRemaining(0);
        setSource('none');
      }
    };

    ACTIVITY_EVENTS.forEach((ev) => document.addEventListener(ev, handleActivity, true));
    return () => {
      ACTIVITY_EVENTS.forEach((ev) => document.removeEventListener(ev, handleActivity, true));
    };
  }, [setSource]);

  return {
    isWarningVisible,
    timeRemaining,
    warningSource,
    extendSession,
    forceLogout,
  };
};
